import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Phone, Car, Mail, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { ProceedingTypeSelect } from "./ProceedingTypeSelect";
import { CompleteApplicationModal, type CompleteTarget } from "./wnioski/CompleteApplicationModal";
import { OfficeFormModal } from "@/features/paper_send/pages/OfficesPage";
import { useAuthStore } from "@/store/auth";
import {
  useApplicationMutations,
  useMyProjectGate,
  useSelectableOffices,
  useParcels,
  useProceedingMutations,
  useProceedings,
  useProceedingTypes,
  useVariants,
} from "../hooks";
import { ProceedingLinksPanel } from "./ProceedingLinksPanel";
import { ParcelResolutionPicker } from "./CaseOutcomeFields";
import { CloseProceedingModal } from "./CloseProceedingModal";
import {
  ITEM_CHANNEL_OPTIONS,
  ITEM_OUTCOME_OPTIONS,
  PROCEEDING_EVENT_TYPE_OPTIONS,
  PROCEEDING_SCOPE_LABEL,
  PROCEEDING_SCOPE_OPTIONS,
  type Proceeding,
  type ProceedingEventType,
  type ProceedingItem,
  type ProceedingPackageBrief,
  type ProceedingScope,
  type PowerProject,
  APPLICATION_STATUS_LABELS,
} from "../types";
import { apiErr, daysAgoLabel, daysSince, fmtDate, fmtTime, plDni } from "../utils";
import {
  appendFiles,
  ApplicationStatusBadge,
  AttachmentLinks,
  EdeliveryLine,
  EmptyState,
  KpaBadge,
  MultiFileInput,
  OutcomeBadge,
  PaperDispatchLine,
  ProceedingScopeBadge,
  ProceedingStatusBadge,
  SectionCard,
} from "./ui";

type RouteVersionOpt = { id: string; label: string };

// Collapse repeated names (office threads list the same gmina once per parcel)
// into "Name (×N)" so the scope line stays legible instead of "X, X, X".
// Kept as a fallback for when the parcels query hasn't resolved owners yet.
function dedupeNames(label: string): string {
  const counts = new Map<string, number>();
  for (const name of label.split(",").map((s) => s.trim()).filter(Boolean)) {
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts].map(([name, n]) => (n > 1 ? `${name} (×${n})` : name)).join(", ");
}

// Polish plural for "działka": 1 → działka, 2–4 (not 12–14) → działki, else działek.
function plDziałki(n: number): string {
  const t10 = n % 10;
  const t100 = n % 100;
  if (n === 1) return "1 działka";
  if (t10 >= 2 && t10 <= 4 && !(t100 >= 12 && t100 <= 14)) return `${n} działki`;
  return `${n} działek`;
}

// owner id → the parcel it sits on, so owner/office threads can show "N działek"
// (the property is implied) instead of repeating the gmina name "(×N)".
type OwnerParcelInfo = { parcelId: string; teryt: string; street: string; fullName: string };
// One parcel in a thread's scope, with the owners (in scope) sitting on it.
type ScopeParcel = { parcelId: string; teryt: string; street: string; owners: string[] };

// One selectable owner in the scope picker; `office` marks an authority-owned
// parcel (urząd) vs a private one (prywatna), so the picker can narrow the list
// to the ownership type the chosen proceeding scope actually targets.
type OwnerItem = { id: string; label: string; office: boolean };

// OWNER (WPW) cases target private owners; OFFICE cases target authority owners.
// TECHNICAL scopes by parcel and never uses this.
function ownersForScope(items: OwnerItem[], scope: ProceedingScope): OwnerItem[] {
  if (scope === "office") return items.filter((o) => o.office);
  if (scope === "owner") return items.filter((o) => !o.office);
  return items;
}

// Reusable filterable checkbox list (owners for OWNER/OFFICE, parcels for TECHNICAL).
function ScopePicker({
  items,
  selected,
  onToggle,
  onToggleMany,
  placeholder,
}: {
  items: { id: string; label: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  // Bulk-select/deselect the currently visible (filtered) items, if provided.
  onToggleMany?: (ids: string[], selected: boolean) => void;
  placeholder: string;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase())),
    [items, q],
  );
  // Whether every currently visible item is already selected → the button flips to "deselect".
  const allVisibleSelected = filtered.length > 0 && filtered.every((i) => selected.has(i.id));
  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2 space-y-1.5">
      <input className="input-base text-[11px] py-1" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="max-h-40 overflow-y-auto space-y-0.5">
        {filtered.length === 0 && <p className="text-[11px] font-mono text-[var(--color-text-muted)] px-1">Brak pozycji.</p>}
        {filtered.map((i) => (
          <label key={i.id} className="flex items-center gap-1.5 text-[11px] font-mono cursor-pointer px-1 py-0.5 hover:bg-[var(--color-bg-surface)]">
            <input type="checkbox" checked={selected.has(i.id)} onChange={() => onToggle(i.id)} />
            {i.label}
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="section-label">{selected.size} zaznaczonych</p>
        {onToggleMany && filtered.length > 0 && (
          <button
            type="button"
            className="section-label normal-case tracking-normal text-[var(--color-primary)] hover:underline"
            onClick={() => onToggleMany(filtered.map((i) => i.id), !allVisibleSelected)}
          >
            {allVisibleSelected ? "Odznacz wszystkie" : "Zaznacz wszystkie"}
          </button>
        )}
      </div>
    </div>
  );
}

const EVENT_ICON: Record<Exclude<ProceedingEventType, "">, typeof Phone> = {
  telefon: Phone,
  wyjazd: Car,
  mail: Mail,
  inne: MessageSquare,
};

// ── inline edit panel for a thread item (metadata per kind + attachments) ─────
// Application: draft — anyone; approved/submitted — can_submit. Response/event: always
// can_submit (backend gate). Attachment add/remove hit the API IMMEDIATELY; "Zapisz"
// covers only the metadata fields.
function ItemEditPanel({
  item,
  connectionId,
  onClose,
  onError,
}: {
  item: ProceedingItem;
  connectionId: string;
  onClose: () => void;
  onError: (e: unknown) => void;
}) {
  const m = useProceedingMutations(connectionId);
  const [edit, setEdit] = useState({
    note: item.note ?? "",
    our_ref: item.our_ref ?? "",
    channel: item.channel ?? "",
    channel_ref: item.channel_ref ?? "",
    date_sent: item.date_sent ?? "",
    office_ref: item.office_ref ?? "",
    date_received: item.date_received ?? "",
    event_type: item.event_type ?? "",
  });
  // Nowe załączniki dokładane w panelu (wgrywane osobnym przyciskiem, od razu).
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const set = (k: keyof typeof edit) => (v: string) => setEdit((p) => ({ ...p, [k]: v }));

  const save = () => {
    const payload =
      item.kind === "application"
        ? { note: edit.note, our_ref: edit.our_ref, channel: edit.channel, channel_ref: edit.channel_ref, date_sent: edit.date_sent }
        : item.kind === "response"
          ? { note: edit.note, office_ref: edit.office_ref, date_received: edit.date_received }
          : { note: edit.note, event_type: edit.event_type };
    m.updateItem.mutate({ itemId: item.id, payload }, { onSuccess: onClose, onError });
  };

  return (
    <div className="mt-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 space-y-2">
      {item.kind === "application" && (
        <div className="grid sm:grid-cols-2 gap-2">
          <label className="block">
            <span className="section-label">Nasz znak</span>
            <input className="input-base mt-1" value={edit.our_ref}
              onChange={(e) => set("our_ref")(e.target.value)} />
          </label>
          <label className="block">
            <span className="section-label">Kanał wysyłki</span>
            <select className="input-base mt-1" value={edit.channel}
              onChange={(e) => set("channel")(e.target.value)}>
              <option value="">— kanał —</option>
              {ITEM_CHANNEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="section-label">Nr UPO / nadania</span>
            <input className="input-base mt-1" value={edit.channel_ref}
              onChange={(e) => set("channel_ref")(e.target.value)} />
          </label>
          <label className="block">
            <span className="section-label">Data wysłania</span>
            <DatePicker className="mt-1" value={edit.date_sent}
              max={new Date().toISOString().slice(0, 10)}
              onChange={set("date_sent")} />
          </label>
        </div>
      )}
      {item.kind === "response" && (
        <div className="grid sm:grid-cols-2 gap-2">
          <label className="block">
            <span className="section-label">Znak urzędu</span>
            <input className="input-base mt-1" value={edit.office_ref}
              onChange={(e) => set("office_ref")(e.target.value)} />
          </label>
          <label className="block">
            <span className="section-label">Data wpływu</span>
            <DatePicker className="mt-1" value={edit.date_received}
              onChange={set("date_received")} />
          </label>
        </div>
      )}
      {item.kind === "event" && (
        <label className="block sm:max-w-xs">
          <span className="section-label">Typ zdarzenia</span>
          <select className="input-base mt-1" value={edit.event_type}
            onChange={(e) => set("event_type")(e.target.value)}>
            {PROCEEDING_EVENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      )}
      <label className="block">
        <span className="section-label">Notatka / treść</span>
        <textarea className="input-base mt-1" rows={2} value={edit.note}
          onChange={(e) => set("note")(e.target.value)} />
      </label>
      {/* Załączniki: usunięcie i dogranie działają OD RAZU (osobne akcje API),
          niezależnie od „Zapisz", które dotyczy tylko metadanych. */}
      <div>
        <span className="section-label">Załączniki</span>
        {item.files.length > 0 ? (
          <ul className="mt-1 space-y-0.5">
            {item.files.map((f) => (
              <li key={f.id} className="flex items-center gap-2 text-[11px] font-mono">
                {f.url ? (
                  <a href={f.url} target="_blank" rel="noreferrer"
                    className="text-[var(--color-primary)] hover:underline truncate">
                    {f.original_filename}
                  </a>
                ) : (
                  <span className="truncate">{f.original_filename}</span>
                )}
                <button
                  type="button"
                  className="text-red-600 hover:underline shrink-0"
                  title="Usuń załącznik (od razu)"
                  onClick={() => {
                    if (!window.confirm(`Usunąć załącznik „${f.original_filename}”?`)) return;
                    m.deleteItemFile.mutate({ itemId: item.id, fileId: f.id }, { onError });
                  }}
                >
                  ✕ usuń
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">Brak załączników</p>
        )}
        <div className="mt-1.5">
          <MultiFileInput files={newFiles} onChange={setNewFiles} label="Dodaj załączniki" />
          {newFiles.length > 0 && (
            <Button
              size="sm"
              variant="secondary"
              className="mt-1"
              loading={m.uploadItemFiles.isPending}
              onClick={() => {
                m.uploadItemFiles.mutate(
                  { itemId: item.id, files: newFiles },
                  { onSuccess: () => setNewFiles([]), onError },
                );
              }}
            >
              Wgraj ({newFiles.length})
            </Button>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-1.5">
        <Button size="sm" variant="ghost" onClick={onClose}>Anuluj</Button>
        <Button size="sm" loading={m.updateItem.isPending} onClick={save}>Zapisz</Button>
      </div>
    </div>
  );
}

// ── date + "N dni temu" counter ───────────────────────────────────────────────
// Every date in the thread (wysłano / wpływ / utworzono / zatwierdzono…) carries a
// day counter to today, so nobody has to do the arithmetic on a KPA clock by hand.
function DateAge({ label, iso, withTime = false, className = "" }: {
  label?: string;
  iso: string | null | undefined;
  withTime?: boolean;
  className?: string;
}) {
  if (!iso) return null;
  const age = daysAgoLabel(iso);
  return (
    <span className={`whitespace-nowrap ${className}`}>
      {label && <span className="text-[var(--color-text-muted)]">{label} </span>}
      {fmtDate(iso)}{withTime ? `, ${fmtTime(iso)}` : ""}
      {age && <span className="text-[var(--color-text-muted)]"> ({age})</span>}
    </span>
  );
}

// ── shared shell for a thread position: rail + header (label · meta · actions) + body
function ItemShell({ rail, label, meta, actions, children }: {
  rail: string;
  label: React.ReactNode;
  meta: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <li className="grid grid-cols-[3px_1fr] gap-x-3 py-2.5">
      <div className={`self-stretch rounded-sm ${rail}`} />
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="flex items-center gap-1.5 text-xs font-mono min-w-[11rem] shrink-0">{label}</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-mono text-[var(--color-text-secondary)] min-w-0 flex-1">
            {meta}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-1.5 ml-auto shrink-0">{actions}</div>}
        </div>
        {children}
      </div>
    </li>
  );
}

const DELETE_BTN = "text-red-600";

// „Zakończ (odpowiedź wpłynęła)" pakietu prosto z karty sprawy: ten sam modal i ta sama
// akcja co na stronie pakietu — odpowiedź trafia do sprawy jako pozycja i zamyka ją.
function CompleteFromCase({ pkg, target, onClose }: {
  pkg: ProceedingPackageBrief;
  target: CompleteTarget["proceeding_info"];
  onClose: () => void;
}) {
  const appM = useApplicationMutations(pkg.id);
  return (
    <CompleteApplicationModal
      app={{ number: pkg.number, proceeding_info: target }}
      open
      onClose={onClose}
      pending={appM.complete.isPending}
      error={appM.complete.error ? apiErr(appM.complete.error) : null}
      onConfirm={(v) => appM.complete.mutate({ id: pkg.id, ...v }, { onSuccess: onClose })}
    />
  );
}

// ── one thread position (application / response / event) ─────────────────────
function ItemRow({
  item,
  connectionId,
  canApprove,
  canSubmit,
  proceedingId,
  proceedingScope,
  proceedingStatus,
  projectId,
  completeTarget = null,
  freePackages = [],
}: {
  item: ProceedingItem;
  connectionId: string;
  canApprove: boolean;
  canSubmit: boolean;
  proceedingId?: string;
  proceedingScope?: string;
  proceedingStatus?: string;
  /** Id przedsięwzięcia (hub) — linki do modułu Wnioski / rejestru decyzji. */
  projectId?: string;
  /** Sprawa w skrócie dla modalu „Zakończ pakiet" (rozstrzygnięcie + właściciele). */
  completeTarget?: CompleteTarget["proceeding_info"];
  /** Pakiety sprawy bez pozycji — do wskazania, którą pozycją „wniosek" jest ich pismo. */
  freePackages?: ProceedingPackageBrief[];
}) {
  const m = useProceedingMutations(connectionId);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [uzupOk, setUzupOk] = useState<number | null>(null);
  // „Zakończ (odpowiedź wpłynęła)" pakietu stojącego za tą pozycją — tylko złożony pakiet
  // w OTWARTEJ sprawie (zamknięta i tak już go zakończyła).
  const [completeOpen, setCompleteOpen] = useState(false);
  const pkg = item.official_application;
  const canCompletePkg = !!pkg && pkg.status === "submitted" && proceedingStatus === "open";
  // Którą pozycją jest pismo pakietu: status pozycji idzie potem za pakietem (złożony / zakończony).
  const assignPackage = (application: string, target: string | null) => {
    if (!proceedingId) return;
    setActionErr(null);
    m.assignPackageItem.mutate(
      { id: proceedingId, application, item: target },
      { onError: (e) => setActionErr(apiErr(e)) },
    );
  };
  // Submit-time send date (backdatable); prefilled from a backdated draft.
  const [sendDate, setSendDate] = useState(item.date_sent ?? "");
  // Inline correction of item metadata (application draft: anyone; approved/submitted
  // and response/event: can_submit — the ItemEditPanel seeds itself from `item` on mount).
  const [editing, setEditing] = useState(false);
  const canEditItem = item.kind === "application"
    ? item.app_status === "draft" || canSubmit
    : canSubmit;
  const startEdit = () => {
    setActionErr(null);
    setEditing(true);
  };
  const onEditError = (e: unknown) => setActionErr(apiErr(e));
  const remove = (question: string) => {
    if (!window.confirm(question)) return;
    setActionErr(null);
    m.deleteItem.mutate(item.id, { onError: (e) => setActionErr(apiErr(e)) });
  };

  const body = (
    <>
      {item.note && <p className="text-[13px] text-[var(--color-text-secondary)] whitespace-pre-wrap">{item.note}</p>}
      {item.files.length > 0 && <div><AttachmentLinks files={item.files} /></div>}
    </>
  );
  const tail = (
    <>
      {editing && (
        <ItemEditPanel item={item} connectionId={connectionId}
          onClose={() => setEditing(false)} onError={onEditError} />
      )}
      {actionErr && (
        <p className="text-[11px] text-red-600 dark:text-red-400" role="alert">{actionErr}</p>
      )}
    </>
  );
  const editDelete = (question: string, deleteDisabled = false, deleteTitle?: string) =>
    canEditItem && !editing ? (
      <>
        <Button size="sm" variant="ghost" onClick={startEdit}>Edytuj</Button>
        <Button size="sm" variant="ghost" className={DELETE_BTN} loading={m.deleteItem.isPending}
          disabled={deleteDisabled} title={deleteTitle} onClick={() => remove(question)}>
          Usuń
        </Button>
      </>
    ) : null;

  if (item.kind === "application") {
    return (
      <ItemShell
        rail="bg-blue-400"
        label={
          <>
            <span className="text-blue-700 font-bold">→ Wniosek</span>
            <ApplicationStatusBadge status={item.app_status} />
          </>
        }
        meta={
          <>
            {item.our_ref && <span>znak: <span className="text-[var(--color-text-primary)]">{item.our_ref}</span></span>}
            {item.channel_display && <span>{item.channel_display}</span>}
            {/* e-Doręczenie przed akceptacją w DLC_09 nie ma daty wysłania — pismo jest
                tylko złożone („wysłano" pojawia się z akceptacją CEO / dyr. technicznego). */}
            {item.date_sent
              ? <DateAge label="wysłano" iso={item.date_sent} className="text-[var(--color-text-primary)]" />
              : (item.app_status === "submitted" || item.app_status === "completed" || item.app_status === "withdrawn") && item.submitted_at
                ? <DateAge label="złożono" iso={item.submitted_at} withTime className="text-[var(--color-text-primary)]" />
                : <DateAge label="utworzono" iso={item.created_at} withTime />}
            {pkg && (
              <Link
                to={`/power-projects/wnioski/${pkg.id}`}
                className="text-[var(--color-primary)] hover:underline"
                title={`${pkg.type_label} — otwórz pakiet w module Wnioski`}
              >
                Pakiet {pkg.number} · {APPLICATION_STATUS_LABELS[pkg.status] ?? pkg.status} →
              </Link>
            )}
            {pkg && proceedingId && (
              <button
                type="button"
                className="text-[var(--color-text-muted)] hover:text-red-600"
                title="To nie to pismo — odepnij pakiet od tej pozycji (pozycja wraca na „Złożony”)"
                aria-label={`Odepnij pakiet ${pkg.number} od tej pozycji`}
                disabled={m.assignPackageItem.isPending}
                onClick={() => {
                  if (window.confirm(`Odpiąć pakiet ${pkg.number} od tej pozycji? Pakiet zostaje w sprawie.`)) {
                    assignPackage(pkg.id, null);
                  }
                }}
              >
                ✕
              </button>
            )}
            {!pkg && proceedingId && freePackages.length > 0 && item.app_status !== "withdrawn" && (
              <select
                className="input-base !py-0 !text-[11px] w-auto"
                aria-label="Przypisz pakiet do tej pozycji"
                value=""
                disabled={m.assignPackageItem.isPending}
                onChange={(e) => e.target.value && assignPackage(e.target.value, item.id)}
              >
                <option value="">przypisz pakiet…</option>
                {freePackages.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.number} · {(APPLICATION_STATUS_LABELS[a.status] ?? a.status).toLowerCase()}
                  </option>
                ))}
              </select>
            )}
          </>
        }
        actions={
          <>
            {canCompletePkg && (
              <Button size="sm" variant="secondary" onClick={() => setCompleteOpen(true)}>
                Zakończ (odpowiedź wpłynęła)
              </Button>
            )}
            {item.app_status === "draft" && (
              <Button
                size="sm"
                variant="secondary"
                disabled={!canApprove}
                loading={m.approve.isPending}
                title={canApprove ? undefined : "Brak uprawnienia do zatwierdzania (panel Metadane)"}
                onClick={() => {
                  setActionErr(null);
                  m.approve.mutate(item.id, { onError: (e) => setActionErr(apiErr(e)) });
                }}
              >
                Zatwierdź
              </Button>
            )}
            {item.app_status === "approved" && (
              <span className="inline-flex items-center gap-1.5">
                <DatePicker
                  className="!h-7 !py-0 text-[11px] w-[8.5rem]"
                  value={sendDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={setSendDate}
                />
                <Button
                  size="sm"
                  disabled={!canSubmit}
                  loading={m.submit.isPending}
                  title={canSubmit ? undefined : "Brak uprawnienia do składania (panel Metadane)"}
                  onClick={() => {
                    setActionErr(null);
                    m.submit.mutate(
                      { itemId: item.id, dateSent: sendDate || undefined },
                      { onError: (e) => setActionErr(apiErr(e)) },
                    );
                  }}
                >
                  Złóż / wyślij
                </Button>
              </span>
            )}
            {editDelete(
              "Usunąć ten wniosek? Operacja jest nieodwracalna.",
              !!item.paper_dispatch,
              item.paper_dispatch
                ? "Wniosek przekazany do dziennika korespondencji (DLC_02) — nie można go usunąć."
                : undefined,
            )}
          </>
        }
      >
        {body}
        {/* Sam kanał („Papier (DLC_02)") nie mówi, co się z przesyłką stało — dokładamy stan
            wpisu w dzienniku, bo autor pisma nie ma do dziennika wglądu. */}
        {item.paper_dispatch_info && <div><PaperDispatchLine dispatch={item.paper_dispatch_info} /></div>}
        {item.edelivery_info && <div><EdeliveryLine edelivery={item.edelivery_info} /></div>}
        {(item.author_name || item.approved_by_name || item.submitted_by_name) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] font-mono text-[var(--color-text-secondary)]">
            {item.author_name && <span>aut. {item.author_name}</span>}
            {item.approved_by_name && (
              <span className="text-blue-600">
                ✓ zatw. {item.approved_by_name}{item.approved_at && <> · <DateAge iso={item.approved_at} /></>}
              </span>
            )}
            {item.submitted_by_name && (
              <span className="text-emerald-600">
                ✓ złoż. {item.submitted_by_name}{item.submitted_at && <> · <DateAge iso={item.submitted_at} /></>}
              </span>
            )}
          </div>
        )}
        {completeOpen && pkg && (
          <CompleteFromCase pkg={pkg} target={completeTarget} onClose={() => setCompleteOpen(false)} />
        )}
        {tail}
      </ItemShell>
    );
  }

  if (item.kind === "response") {
    const closedLocked = item.closes_thread && proceedingStatus === "closed";
    return (
      <ItemShell
        rail="bg-emerald-400"
        label={
          <>
            <span className="text-emerald-700 font-bold">← Odpowiedź</span>
            {item.closes_thread && item.outcome !== "none" && <OutcomeBadge outcome={item.outcome} />}
          </>
        }
        meta={
          <>
            {item.office_ref && <span>znak: <span className="text-[var(--color-text-primary)]">{item.office_ref}</span></span>}
            {item.date_received
              ? <DateAge label="wpływ" iso={item.date_received} className="text-[var(--color-text-primary)]" />
              : <DateAge label="utworzono" iso={item.created_at} withTime />}
            {item.closes_thread && <span className="text-[var(--color-primary)] font-bold">[zamyka wątek]</span>}
          </>
        }
        actions={editDelete(
          "Usunąć tę odpowiedź? Operacja jest nieodwracalna.",
          closedLocked,
          closedLocked ? "Ta odpowiedź zamknęła sprawę — najpierw otwórz sprawę ponownie." : undefined,
        )}
      >
        {body}
        {item.decisions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {item.decisions.map((d) => (
              <Link
                key={d.id}
                to={`/power-projects/${projectId ?? ""}?tab=wnioski`}
                className="inline-flex items-center gap-1 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-[11px] font-mono hover:border-[var(--color-primary)]"
                title="Decyzja w rejestrze decyzji (zakładka Wnioski)"
              >
                ⚖ Decyzja {d.sign} · {d.kind_display} · {d.finality_display}
              </Link>
            ))}
          </div>
        )}
        {/* 1-klik: odpowiedź = wezwanie do uzupełnienia → bloczek „uzupełnienie" w torach
            działek sprawy (zegar lokalizacji wraca na naszą stronę) */}
        {proceedingId && proceedingScope !== "technical" && !item.closes_thread && (
          uzupOk !== null ? (
            <p className="text-[11px] text-amber-700 font-mono">
              ✓ Oznaczono jako wezwanie do uzupełnienia ({uzupOk} dz. — zegar po naszej stronie)
            </p>
          ) : (
            <button
              className="text-[11px] text-amber-700 underline"
              title="Dokłada aktywny element „Uzupełnienie” do toru każdej działki tej sprawy — piłka wraca do nas z własnym zegarem (default 14 dni)."
              onClick={() => m.markUzupelnienie.mutate(
                { id: proceedingId, note: item.office_ref ? `Wezwanie ${item.office_ref}` : "" },
                { onSuccess: (r) => setUzupOk(r.parcels_marked) },
              )}
            >
              Oznacz jako wezwanie do uzupełnienia
            </button>
          )
        )}
        {tail}
      </ItemShell>
    );
  }

  // EVENT
  const Icon = item.event_type ? EVENT_ICON[item.event_type] : MessageSquare;
  return (
    <ItemShell
      rail="bg-[var(--color-border-strong)]"
      label={
        <>
          <Icon size={13} className="text-[var(--color-text-secondary)]" />
          <span className="font-bold text-[var(--color-text-secondary)]">{item.event_type_display || "Zdarzenie"}</span>
        </>
      }
      meta={
        <>
          <DateAge iso={item.created_at} withTime className="text-[var(--color-text-primary)]" />
          {item.created_by_name && <span>{item.created_by_name}</span>}
        </>
      }
      actions={editDelete("Usunąć to zdarzenie? Operacja jest nieodwracalna.")}
    >
      {body}
      {tail}
    </ItemShell>
  );
}

// ── po odpowiedzi urzędu: pytanie o zakończenie pakietu (i tym samym sprawy) ─────
// Sprawa i pakiet z modułu Wnioski domykają się razem (zamknięcie sprawy kończy pakiet), ale
// CZY to już koniec — decyduje człowiek: odpowiedź urzędu bywa wezwaniem do uzupełnienia.
// Niezłożony pakiet z modułu Wnioski trzyma sprawę otwartą — backend odmawia zamknięcia
// (ProceedingService.close_blocker), więc UI nie proponuje zamykania, tylko mówi, na co czeka.
const closeBlockers = (p: Proceeding) => p.official_applications.filter((a) => a.blocks_close);
const closeBlockedText = (blockers: Proceeding["official_applications"]) =>
  `Zamknięcie czeka na wysyłkę: ${blockers
    .map((a) => `${a.number} (${(APPLICATION_STATUS_LABELS[a.status] ?? a.status).toLowerCase()})`)
    .join(", ")} — złóż wniosek albo odepnij go od sprawy.`;

// Odpowiedź urzędu bez „zamyka wątek” przy ZŁOŻONYM pakiecie: pytamy, czy to już koniec —
// ten sam modal co „Zamknij sprawę”, tylko z wstępem o pakiecie i „Nie teraz”.
function ClosePackagePrompt({
  proceeding,
  onClose,
  connectionId,
}: {
  proceeding: Proceeding;
  onClose: () => void;
  connectionId: string;
}) {
  const packages = proceeding.official_applications.filter((a) => a.status === "submitted");
  return (
    <CloseProceedingModal
      proceeding={proceeding}
      connectionId={connectionId}
      onClose={onClose}
      title="Odpowiedź urzędu zarejestrowana"
      intro={
        <p>
          Do tej sprawy jest podpięty pakiet <b>{packages.map((a) => a.number).join(", ")}</b>.
          Zamknąć pakiet i tym samym sprawę? Sprawa dostanie wskazane rozstrzygnięcie, a pakiet
          status „Zakończony”. Jeśli to dopiero wezwanie do uzupełnienia — wybierz „Nie teraz”.
        </p>
      }
      cancelLabel="Nie teraz"
      confirmLabel="Zamknij sprawę i zakończ pakiet"
    />
  );
}

// ── "which case am I adding to" banner for the per-case modals ─────────────────
// Several cases on a project often share type and office — the banner spells out the
// target (type, office, scope, KPA, founding date) so a position/parcel lands in the right one.
function ProceedingTargetBanner({ proceeding: p, scopeLabel }: { proceeding: Proceeding; scopeLabel: string }) {
  return (
    <div className="mb-4 border-l-4 border-[var(--color-primary)] bg-[var(--color-bg-elevated)] px-3 py-2">
      <span className="section-label block">Dodajesz do sprawy</span>
      <div className="mt-1 font-mono text-sm font-bold text-[var(--color-text-primary)] flex flex-wrap items-center gap-1.5">
        <ProceedingScopeBadge scope={p.scope} />
        {p.type_name}
        {p.type_note && <span className="text-[var(--color-text-secondary)] font-normal">— {p.type_note}</span>}
      </div>
      <dl className="mt-1.5 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-[13px] text-[var(--color-text-secondary)]">
        {p.office_name && (
          <>
            <dt className="section-label self-center">Urząd</dt>
            <dd className="text-[var(--color-text-primary)]">{p.office_name}</dd>
          </>
        )}
        <dt className="section-label self-center">Zakres</dt>
        <dd>{scopeLabel}</dd>
        {p.route_version_label && (
          <>
            <dt className="section-label self-center">Trasa</dt>
            <dd>{p.route_version_label}</dd>
          </>
        )}
        <dt className="section-label self-center">Założono</dt>
        <dd>
          {fmtDate(p.created_at)}
          <span className="text-[var(--color-text-muted)]"> · pozycji: {p.items.length}</span>
        </dd>
      </dl>
    </div>
  );
}

// ── add-item composer (application / response / event), each multifile ────────
function ItemComposer({
  proceeding,
  connectionId,
  scopeLabel,
  onClose,
}: {
  proceeding: Proceeding;
  connectionId: string;
  scopeLabel: string;
  onClose: () => void;
}) {
  const m = useProceedingMutations(connectionId);
  const [kind, setKind] = useState<"application" | "response" | "event">("application");
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState("");
  // application fields
  const [ourRef, setOurRef] = useState("");
  const [channel, setChannel] = useState("");
  const [channelRef, setChannelRef] = useState("");
  const [dateSent, setDateSent] = useState("");
  // response fields
  const [officeRef, setOfficeRef] = useState("");
  const [dateReceived, setDateReceived] = useState("");
  const [closes, setCloses] = useState(false);
  const [outcome, setOutcome] = useState("positive");
  // Rozstrzygnięcie częściowe: działki uzgodnione (ta sama lista co w „Zamknij sprawę”).
  const [resolved, setResolved] = useState<Set<string>>(new Set());
  // event fields
  const [eventType, setEventType] = useState<Exclude<ProceedingEventType, "">>("telefon");
  const partialWithoutParcels = kind === "response" && closes && outcome === "partial" && resolved.size === 0;

  const reset = () => {
    setFiles([]); setNote(""); setOurRef(""); setChannel(""); setChannelRef(""); setDateSent("");
    setOfficeRef(""); setDateReceived(""); setCloses(false); setOutcome("positive");
    setResolved(new Set()); setEventType("telefon");
  };

  const submit = () => {
    const fd = new FormData();
    if (note) fd.append("note", note);
    appendFiles(fd, files);
    if (kind === "application") {
      if (ourRef) fd.append("our_ref", ourRef);
      if (channel) fd.append("channel", channel);
      if (channelRef) fd.append("channel_ref", channelRef);
      if (dateSent) fd.append("date_sent", dateSent);
      m.addApplication.mutate({ id: proceeding.id, fd }, { onSuccess: onClose });
    } else if (kind === "response") {
      if (officeRef) fd.append("office_ref", officeRef);
      if (dateReceived) fd.append("date_received", dateReceived);
      fd.append("closes_thread", String(closes));
      if (closes) {
        fd.append("outcome", outcome);
        if (outcome === "partial") {
          for (const id of resolved) fd.append("resolved_parcels", id);
        }
      }
      const askClose = !closes && submittedPackages.length > 0 && blockers.length === 0;
      m.addResponse.mutate(
        { id: proceeding.id, fd },
        // The "zakończyć pakiet?" prompt needs this component mounted — keep the (reset)
        // composer open under it and close both once the prompt is answered.
        { onSuccess: () => { if (askClose) { reset(); setClosePrompt(true); } else onClose(); } },
      );
    } else {
      fd.append("event_type", eventType);
      m.addEvent.mutate({ id: proceeding.id, fd }, { onSuccess: onClose });
    }
  };

  const busy = m.addApplication.isPending || m.addResponse.isPending || m.addEvent.isPending;
  const saveError = m.addApplication.error ?? m.addResponse.error ?? m.addEvent.error;
  const blockers = closeBlockers(proceeding);
  // Odpowiedź urzędu bez „zamyka wątek": gdy do sprawy podpięty jest ZŁOŻONY pakiet z modułu
  // Wnioski, pytamy, czy zakończyć pakiet i tym samym sprawę — decyzja należy do człowieka.
  const submittedPackages = proceeding.official_applications.filter((a) => a.status === "submitted");
  const [closePrompt, setClosePrompt] = useState(false);

  return (
    <>
    <Modal
      open
      // Escape reaches this modal too while the nested prompt is open — let the prompt handle it.
      onClose={() => { if (!closePrompt) onClose(); }}
      title="Nowa pozycja sprawy"
      size="lg"
    >
    <div className="space-y-3">
      <ProceedingTargetBanner proceeding={proceeding} scopeLabel={scopeLabel} />
      <div className="flex gap-1">
        {([["application", "Wniosek (nasz)"], ["response", "Odpowiedź"], ["event", "Zdarzenie"]] as const).map(([v, lbl]) => (
          <Button key={v} size="sm" variant={kind === v ? "primary" : "ghost"} onClick={() => setKind(v)}>{lbl}</Button>
        ))}
      </div>

      {kind === "application" && (
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="section-label">Nasz znak</span>
            <input className="input-base mt-1" placeholder="np. ELEKTIM/98/39/2026" value={ourRef} onChange={(e) => setOurRef(e.target.value)} />
          </label>
          <label className="block">
            <span className="section-label">Kanał wysyłki</span>
            <select className="input-base mt-1" value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="">— kanał —</option>
              {ITEM_CHANNEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="section-label">Data wysłania (wpis wsteczny)</span>
            <DatePicker
              className="mt-1"
              value={dateSent}
              max={new Date().toISOString().slice(0, 10)}
              onChange={setDateSent}
            />
          </label>
          {(channel === "edoreczenia" || channel === "paper") && (
            <label className="block sm:col-span-2">
              <span className="section-label">Nr UPO / nadania</span>
              <input className="input-base mt-1" placeholder="Identyfikator UPO lub numer nadania" value={channelRef} onChange={(e) => setChannelRef(e.target.value)} />
            </label>
          )}
        </div>
      )}

      {kind === "response" && (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="section-label">Znak urzędu</span>
              <input className="input-base mt-1" placeholder="Sygnatura pisma urzędu" value={officeRef} onChange={(e) => setOfficeRef(e.target.value)} />
            </label>
            <label className="block">
              <span className="section-label">Data wpływu</span>
              <DatePicker className="mt-1" value={dateReceived} onChange={setDateReceived} />
            </label>
          </div>
          <label className="flex items-center gap-2 font-mono text-xs">
            <input type="checkbox" checked={closes} disabled={blockers.length > 0}
              onChange={(e) => setCloses(e.target.checked)} /> Zamyka wątek (decyzja / rozstrzygnięcie)
          </label>
          {blockers.length > 0 && (
            <p className="text-[11px] font-mono text-amber-700 dark:text-amber-400">{closeBlockedText(blockers)}</p>
          )}
          {closes && (
            <div className="space-y-2">
              <label className="block sm:max-w-xs">
                <span className="section-label">Rozstrzygnięcie</span>
                <select className="input-base mt-1" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                  {ITEM_OUTCOME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              {outcome === "partial" && (
                <ParcelResolutionPicker
                  proceedingId={proceeding.id}
                  scope={proceeding.scope}
                  selected={resolved}
                  setSelected={setResolved}
                />
              )}
            </div>
          )}
        </div>
      )}

      {kind === "event" && (
        <label className="block sm:max-w-xs">
          <span className="section-label">Typ zdarzenia</span>
          <select className="input-base mt-1" value={eventType} onChange={(e) => setEventType(e.target.value as Exclude<ProceedingEventType, "">)}>
            {PROCEEDING_EVENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      )}

      <label className="block">
        <span className="section-label">Notatka / treść</span>
        <textarea
          className="input-base mt-1"
          rows={3}
          placeholder="Treść pisma, ustalenia, uwagi…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <MultiFileInput files={files} onChange={setFiles} />
      {saveError && <p className="text-xs text-red-600" role="alert">{apiErr(saveError)}</p>}
      <div className="flex justify-end gap-2 pt-3 border-t border-[var(--color-border-muted)]">
        <Button size="sm" variant="ghost" onClick={onClose}>Anuluj</Button>
        <Button size="sm" loading={busy} disabled={partialWithoutParcels} onClick={submit}>
          {kind === "application" ? "Dodaj wniosek" : kind === "response" ? "Dodaj odpowiedź" : "Dodaj zdarzenie"}
        </Button>
      </div>
    </div>
    </Modal>
    {closePrompt && (
      <ClosePackagePrompt
        proceeding={proceeding} onClose={() => { setClosePrompt(false); onClose(); }}
        connectionId={connectionId}
      />
    )}
    </>
  );
}

// ── one proceeding (thread) ──────────────────────────────────────────────────
function ProceedingCard({
  p,
  connectionId,
  ownerOptions,
  parcelOptions,
  ownerParcelInfo,
  parcelTeryt,
  projectId,
  canApprove,
  canSubmit,
  focused = false,
}: {
  p: Proceeding;
  connectionId: string;
  ownerOptions: OwnerItem[];
  parcelOptions: { id: string; label: string }[];
  ownerParcelInfo: Map<string, OwnerParcelInfo>;
  /** id działki → TERYT (do wypisania działek sprawy przy porównaniu z pakietem). */
  parcelTeryt: Map<string, string>;
  projectId: string;
  canApprove: boolean;
  canSubmit: boolean;
  /** Sprawa wskazana w adresie (?proceeding=<id>) — np. z „!" przy statusie działki:
   *  karta przewija się w pole widzenia, podświetla i otwiera od razu pisma. */
  focused?: boolean;
}) {
  const m = useProceedingMutations(connectionId);
  const [showItems, setShowItems] = useState(focused);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused) rootRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focused]);
  const [showScope, setShowScope] = useState(false);
  const [composing, setComposing] = useState(false);
  const [closing, setClosing] = useState(false);
  // Blockers (pismo w DLC_02/DLC_09, zabezpieczone tytuły, wniosek) wracają z backendu
  // dopiero przy próbie usunięcia — pokazujemy je pod kartą sprawy.
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const isClosed = p.status === "closed";

  // "+ dodaj działki" (open cases only): extend the thread's scope with the same picker the
  // add-case form uses — owners for OWNER/OFFICE, parcels for TECHNICAL. Add-only: the picker
  // hides already-attached items and the PATCH sends the union so nothing is dropped.
  const ownerScope = p.scope === "owner" || p.scope === "office";
  const [addingScope, setAddingScope] = useState(false);
  const [selScope, setSelScope] = useState<Set<string>>(new Set());
  const addableScopeItems = useMemo(() => {
    const attached = new Set(ownerScope ? p.owner_ids : p.parcel_ids);
    // Offer only owners of the ownership type this case's scope targets (urząd vs prywatna).
    const base = ownerScope ? ownersForScope(ownerOptions, p.scope) : parcelOptions;
    return base.filter((i) => !attached.has(i.id));
  }, [ownerScope, p.scope, p.owner_ids, p.parcel_ids, ownerOptions, parcelOptions]);
  const toggleScope = (id: string) =>
    setSelScope((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const toggleManyScope = (ids: string[], value: boolean) =>
    setSelScope((prev) => {
      const next = new Set(prev);
      if (value) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  const addScope = () => {
    if (selScope.size === 0) return;
    const payload = ownerScope
      ? { owners: [...new Set([...p.owner_ids, ...selScope])] }
      : { parcels: [...new Set([...p.parcel_ids, ...selScope])] };
    m.update.mutate(
      { id: p.id, payload },
      { onSuccess: closeAddScope },
    );
  };
  const closeAddScope = () => { setAddingScope(false); setSelScope(new Set()); };

  // OWNER/OFFICE threads scope by owner; the parcel is implied, so resolve the
  // owners to their parcels and show "N działek" + a "rozwiń" list of which ones.
  // Returns null while parcels are still loading (so we fall back to names).
  const scopeParcels = useMemo<ScopeParcel[] | null>(() => {
    if (p.scope === "technical" || p.owner_ids.length === 0) return null;
    const resolved = p.owner_ids.map((id) => ownerParcelInfo.get(id));
    if (resolved.some((r) => !r)) return null; // not all owners resolved yet
    const byParcel = new Map<string, ScopeParcel>();
    for (const r of resolved as OwnerParcelInfo[]) {
      const g = byParcel.get(r.parcelId) ?? { parcelId: r.parcelId, teryt: r.teryt, street: r.street, owners: [] };
      if (!g.owners.includes(r.fullName)) g.owners.push(r.fullName);
      byParcel.set(r.parcelId, g);
    }
    return [...byParcel.values()].sort((a, b) => a.teryt.localeCompare(b.teryt));
  }, [p.scope, p.owner_ids, ownerParcelInfo]);

  // Działki sprawy i działki pakietu to OSOBNE relacje — gdy się rozjeżdżają, mówimy to
  // przy zakresie i po rozwinięciu wypisujemy działki pakietu, żeby dało się porównać.
  // Pakiet zakotwiczony na działce (wpw_parcel: pakiet WPW, art. 124) jest per (działka,
  // właściciel) — sprawa zlecenia WPW ma ich tyle, ilu właścicieli, więc jeden pakiet
  // NIGDY nie obejmuje całej sprawy. Dla niego liczy się tylko, czy jego działka i
  // właściciel mieszczą się w sprawie; „tylko w sprawie" byłoby fałszywym alarmem.
  const scopeMismatches = useMemo(() => {
    const caseIds = new Set(p.parcel_ids);
    const caseOwners = new Set(p.owner_ids);
    return p.official_applications
      .map((a) => {
        const anchored = !!a.wpw_parcel;
        const appIds = new Set(a.parcels.map((x) => x.id));
        const missingInCase = a.parcels.filter((x) => !caseIds.has(x.id));
        const onlyInCase = anchored
          ? []
          : p.parcel_ids.filter((id) => !appIds.has(id)).map((id) => parcelTeryt.get(id) ?? id);
        const ownerOutside =
          anchored && !!a.wpw_owner && caseOwners.size > 0 && !caseOwners.has(a.wpw_owner);
        return { app: a, missingInCase, onlyInCase, ownerOutside };
      })
      .filter((x) => x.missingInCase.length > 0 || x.onlyInCase.length > 0 || x.ownerOutside);
  }, [p.official_applications, p.parcel_ids, p.owner_ids, parcelTeryt]);
  const [showPkgScope, setShowPkgScope] = useState<string | null>(null);
  // Sprawa w skrócie dla „Zakończ pakiet" z pozycji (rozstrzygnięcie + działki sprawy).
  const completeTarget = useMemo<CompleteTarget["proceeding_info"]>(() => ({
    id: p.id,
    type_name: p.type_name,
    scope: p.scope,
  }), [p.id, p.type_name, p.scope]);

  const scopeLabel =
    p.scope === "technical"
      ? (p.parcel_ids.length ? plDziałki(p.parcel_ids.length) : "całe przedsięwzięcie")
      : scopeParcels
        ? plDziałki(scopeParcels.length)
        : (p.owners_label
            ? dedupeNames(p.owners_label)
            : (p.owner_ids.length ? `${p.owner_ids.length} właścicieli` : "całe przedsięwzięcie"));

  // Key dates for the header "Daty" column: the latest SUBMITTED application (this is
  // what drives the KPA clock) and the latest response from the office.
  // Pismo e-Doręczeń przed akceptacją w DLC_09 jest złożone, nie wysłane — nie wchodzi tu
  // nawet datą złożenia (to fallback tylko dla starych pism bez daty wysłania).
  const lastSent = useMemo(() => {
    const dates = p.items
      .filter((it) => it.kind === "application" && (it.app_status === "submitted" || it.app_status === "completed"))
      .filter((it) => it.date_sent || !it.edelivery_info?.awaiting_approval)
      .map((it) => it.date_sent ?? it.submitted_at)
      .filter((d): d is string => !!d);
    return dates.sort()[dates.length - 1] ?? null;
  }, [p.items]);
  // Pakiety sprawy, których pismo nie ma jeszcze pozycji „wniosek" (znak nie wskazał numeru).
  // Pakiet WPW idzie do negocjatora, nie do urzędu — nie ma pisma w sprawie.
  const freePackages = p.official_applications.filter(
    (a) => !a.proceeding_item && a.status !== "cancelled" && a.application_type !== "wpw_pakiet",
  );
  const unassignedLetters = p.items.filter(
    (it) => it.kind === "application" && !it.official_application && it.app_status !== "withdrawn",
  ).length;
  const awaitingEdelivery = p.items.some(
    (it) => it.kind === "application" && it.app_status === "submitted" && !it.date_sent
      && !!it.edelivery_info?.awaiting_approval,
  );
  const lastResponse = useMemo(() => {
    const dates = p.items
      .filter((it) => it.kind === "response")
      .map((it) => it.date_received ?? it.created_at.slice(0, 10))
      .filter((d): d is string => !!d);
    return dates.sort()[dates.length - 1] ?? null;
  }, [p.items]);
  const counts = useMemo(() => {
    const c = { application: 0, response: 0, event: 0 };
    for (const it of p.items) c[it.kind] = (c[it.kind] ?? 0) + 1;
    return c;
  }, [p.items]);
  const kpaDays = daysSince(p.kpa_deadline);
  const kpaAge =
    kpaDays == null ? "" : kpaDays === 0 ? "dzisiaj" : kpaDays > 0 ? `${plDni(kpaDays)} po terminie` : `za ${plDni(-kpaDays)}`;

  return (
    <div
      ref={rootRef}
      id={`proceeding-${p.id}`}
      className={`card p-4${focused ? " border-[var(--color-primary)] shadow-hard-red" : ""}`}
    >
      {/* Header: title + scope (left) · key dates with day counters (middle) · status (right) */}
      <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
        <div className="min-w-0 flex-1 basis-72">
          <div className="font-mono text-sm font-bold text-[var(--color-text-primary)] flex flex-wrap items-center gap-1.5">
            <ProceedingScopeBadge scope={p.scope} />
            {p.type_name}
            {p.type_note && <span className="text-[var(--color-text-secondary)] font-normal">— {p.type_note}</span>}
            {p.auto_origin && (
              <Badge color="gray" title={`Sprawa założona automatycznie ${p.auto_origin.label}`}>
                AUTO
              </Badge>
            )}
          </div>
          <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[13px] text-[var(--color-text-secondary)]">
            {/* Sprawa założona przez system — skąd się wzięła (wniosek / zlecenie WPW). */}
            {p.auto_origin && (
              <>
                <dt className="section-label self-center">Pochodzenie</dt>
                <dd>
                  założona automatycznie{" "}
                  {p.auto_origin.kind === "application" ? (
                    <>
                      przy wysyłce wniosku{" "}
                      <Link to={`/power-projects/wnioski/${p.auto_origin.application_id}`}
                            className="text-[var(--color-primary)] hover:underline font-mono">
                        {p.auto_origin.application_number} →
                      </Link>
                    </>
                  ) : (
                    <>
                      przy zleceniu WPW{" "}
                      <Link to={`/orders/${p.auto_origin.order_id}`}
                            className="text-[var(--color-primary)] hover:underline font-mono">
                        {p.auto_origin.order_number} →
                      </Link>
                    </>
                  )}
                </dd>
              </>
            )}
            {p.office_name && (
              <>
                <dt className="section-label self-center">Urząd</dt>
                <dd className="text-[var(--color-text-primary)]">{p.office_name}</dd>
              </>
            )}
            <dt className="section-label self-start pt-0.5">Zakres</dt>
            <dd>
              {scopeLabel}
              {(p.outdated_owner_ids?.length ?? 0) > 0 && (
                <span
                  className="ml-2 px-1 py-px text-[9px] uppercase tracking-wide border border-[var(--color-border)] text-[var(--color-text-muted)]"
                  title="Właściciele tej sprawy oznaczeni jako nieaktualni po ponownie zamówionym wypisie. Sprawa zostaje, ale jej zakończenie czy anulowanie nie zmieni działki, chyba że został w niej aktualny właściciel."
                >
                  nieaktualnych właścicieli: {p.outdated_owner_ids.length}
                </span>
              )}
              {scopeParcels && (
                <button
                  type="button"
                  className="ml-2 text-[var(--color-primary)] hover:underline section-label normal-case tracking-normal"
                  onClick={() => setShowScope((x) => !x)}
                >
                  {showScope ? "ukryj" : "rozwiń"}
                </button>
              )}
              {scopeParcels && showScope && (
                <ul className="mt-1 space-y-0.5">
                  {scopeParcels.map((g) => (
                    <li key={g.parcelId} className="font-mono text-[11px] text-[var(--color-text-secondary)]">
                      <span className="text-[var(--color-text-primary)]">{g.teryt}</span>
                      {g.street && <span className="text-[var(--color-text-muted)]"> · {g.street}</span>}
                      {g.owners.length > 0 && <span> — {g.owners.join(", ")}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {scopeMismatches.map(({ app, missingInCase, onlyInCase, ownerOutside }) => (
                <div key={app.id} className="mt-1 text-[12px]">
                  <span className="text-amber-700 dark:text-amber-400">
                    ⚠ Zakres niezgodny z pakietem {app.number}
                  </span>
                  <button
                    type="button"
                    className="ml-2 text-[var(--color-primary)] hover:underline section-label normal-case tracking-normal"
                    onClick={() => setShowPkgScope((x) => (x === app.id ? null : app.id))}
                  >
                    {showPkgScope === app.id ? "ukryj" : "rozwiń"}
                  </button>
                  {showPkgScope === app.id && (
                    <div className="mt-1 font-mono text-[11px] space-y-0.5">
                      <div>
                        <span className="text-[var(--color-text-muted)]">Działki pakietu ({app.parcels.length}): </span>
                        {app.parcels.map((x, i) => (
                          <span key={x.id}>
                            {i > 0 && ", "}
                            <span
                              className={missingInCase.some((m) => m.id === x.id)
                                ? "text-red-600 dark:text-red-400 font-bold"
                                : "text-[var(--color-text-primary)]"}
                              title={missingInCase.some((m) => m.id === x.id) ? "Brak w sprawie" : undefined}
                            >
                              {x.teryt}
                            </span>
                          </span>
                        ))}
                        {missingInCase.length > 0 && (
                          <span className="text-[var(--color-text-muted)]"> — na czerwono brak w sprawie</span>
                        )}
                      </div>
                      {onlyInCase.length > 0 && (
                        <div>
                          <span className="text-[var(--color-text-muted)]">Tylko w sprawie ({onlyInCase.length}): </span>
                          <span className="text-[var(--color-text-primary)]">{onlyInCase.join(", ")}</span>
                        </div>
                      )}
                      {ownerOutside && (
                        <div>
                          <span className="text-[var(--color-text-muted)]">Właściciel pakietu: </span>
                          <span className="text-red-600 dark:text-red-400 font-bold" title="Brak w sprawie">
                            {app.wpw_owner_name || app.wpw_owner}
                          </span>
                          <span className="text-[var(--color-text-muted)]"> — brak w sprawie</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </dd>
            {p.route_version_label && (
              <>
                <dt className="section-label self-center">Trasa</dt>
                <dd>{p.route_version_label}</dd>
              </>
            )}
          </dl>
          {/* Powiązania sprawy: zlecenie WPW / zamówienia projektu i pakiety z modułu Wnioski
              (pakiet domyka się razem ze sprawą) — lista, odpinanie i picker „Powiąż". */}
          <ProceedingLinksPanel proceeding={p} connectionId={connectionId} />
        </div>

        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[13px] font-mono text-[var(--color-text-secondary)] shrink-0">
          <dt className="section-label self-center">Założono</dt>
          <dd><DateAge iso={p.created_at} /></dd>
          <dt className="section-label self-center">Wysłano wniosek</dt>
          <dd>
            {lastSent
              ? <DateAge iso={lastSent} className="text-[var(--color-text-primary)]" />
              : awaitingEdelivery
                ? <span className="text-amber-700">— złożono, e-Doręczenie czeka na akceptację</span>
                : <span className="text-[var(--color-text-muted)]">— (jeszcze nie złożono)</span>}
          </dd>
          <dt className="section-label self-center">Ost. odpowiedź</dt>
          <dd>
            {lastResponse
              ? <DateAge iso={lastResponse} className="text-[var(--color-text-primary)]" />
              : <span className="text-[var(--color-text-muted)]">—</span>}
          </dd>
          {!isClosed && p.kpa_deadline && (
            <>
              <dt className="section-label self-center">Termin KPA</dt>
              <dd className={p.kpa_overdue ? "text-red-600 dark:text-red-400 font-bold" : ""}>
                {fmtDate(p.kpa_deadline)}
                {kpaAge && <span className={p.kpa_overdue ? "" : "text-[var(--color-text-muted)]"}> ({kpaAge})</span>}
                <span className="text-[var(--color-text-muted)]"> · {p.kpa_days} dni</span>
              </dd>
            </>
          )}
          {isClosed && (
            <>
              <dt className="section-label self-center">Zamknięto</dt>
              <dd>
                <DateAge iso={p.closed_at} />
                {p.closed_by_name && <span className="text-[var(--color-text-muted)]"> · {p.closed_by_name}</span>}
              </dd>
            </>
          )}
          {!isClosed && (
            <>
              <dt className="section-label self-center">Ponaglenia</dt>
              <dd>
                {/* Włączenie przypisuje telefony zalogowanemu; termin = Termin KPA sprawy. */}
                <label
                  className="inline-flex items-center gap-1.5 cursor-pointer"
                  title="Sprawa w kalendarzu ponagleń — termin liczony z Terminu KPA"
                >
                  <input
                    type="checkbox"
                    aria-label="Sprawa w ponagleniach"
                    checked={!!p.control_call_user}
                    disabled={m.setControlCalls.isPending}
                    onChange={(e) =>
                      m.setControlCalls.mutate(
                        { id: p.id, enabled: e.target.checked },
                        { onError: (err) => window.alert(apiErr(err)) },
                      )
                    }
                  />
                  {p.control_call_user
                    ? <span className="text-[var(--color-text-primary)]">{p.control_call_user_name}</span>
                    : <span className="text-[var(--color-text-muted)]">—</span>}
                </label>
              </dd>
            </>
          )}
        </dl>

        <div className="flex flex-col items-end gap-1 shrink-0">
          <ProceedingStatusBadge status={p.status} />
          {isClosed && <OutcomeBadge outcome={p.outcome} />}
          {!isClosed && <KpaBadge deadline={p.kpa_deadline} status={p.kpa_deadline_status} overdue={p.kpa_overdue} />}
        </div>
      </div>

      {isClosed && p.closing_note && (
        <p className="text-[13px] text-[var(--color-text-primary)] mt-3 whitespace-pre-wrap border-l-2 border-[var(--color-border)] pl-2">
          <span className="section-label block">Notatka zamknięcia</span>
          {p.closing_note}
        </p>
      )}
      {isClosed && p.outcome === "partial" && (p.resolved_parcel_ids ?? []).length > 0 && (
        <p className="text-[13px] text-[var(--color-text-primary)] mt-3 border-l-2 border-[var(--color-border)] pl-2">
          <span className="section-label block">
            Działki uzgodnione ({p.resolved_parcel_ids.length} z {Math.max(p.parcel_ids.length, p.resolved_parcel_ids.length)})
          </span>
          <span className="font-mono">
            {p.resolved_parcel_ids.map((id) => parcelTeryt.get(id) ?? id).join(", ")}
          </span>
        </p>
      )}

      {/* Positions: header bar with counts, then the thread (wnioski / odpowiedzi / zdarzenia). */}
      <div className="mt-3 pt-2 border-t border-[var(--color-border-muted)] flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          type="button"
          className="section-label text-[var(--color-primary)] hover:underline"
          onClick={() => setShowItems((x) => !x)}
        >
          {showItems ? "▾ Ukryj" : "▸ Pokaż"} pozycje ({p.items.length})
        </button>
        {p.items.length > 0 && (
          <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
            wnioski: {counts.application} · odpowiedzi: {counts.response} · zdarzenia: {counts.event}
          </span>
        )}
      </div>
      {showItems && freePackages.length > 0 && unassignedLetters > 0 && (
        <p className="mt-1 text-[11px] font-mono text-amber-700 dark:text-amber-400">
          {freePackages.length === 1 ? "Pakiet" : "Pakiety"} {freePackages.map((a) => a.number).join(", ")}{" "}
          {freePackages.length === 1 ? "nie jest przypisany" : "nie są przypisane"} do żadnej pozycji „Wniosek” —
          znak pozycji nie wskazał numeru. Wybierz pakiet przy właściwym wniosku („przypisz pakiet…”),
          żeby status pozycji szedł za pakietem.
        </p>
      )}
      {showItems && p.items.length > 0 && (
        <ul className="mt-1 divide-y divide-[var(--color-border-muted)]">
          {p.items.map((it) => (
            <ItemRow key={it.id} item={it} connectionId={connectionId} canApprove={canApprove}
              canSubmit={canSubmit} proceedingId={p.id} proceedingScope={p.scope}
              proceedingStatus={p.status} projectId={projectId} completeTarget={completeTarget}
              freePackages={freePackages} />
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-2 border-t border-[var(--color-border-muted)]">
        {!isClosed ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => setComposing(true)}>
              + Pozycja (wniosek / odpowiedź / zdarzenie)
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setAddingScope(true)}>
              + dodaj działki
            </Button>
            {closeBlockers(p).length > 0 ? (
              <span className="ml-auto text-[11px] font-mono text-amber-700 dark:text-amber-400">
                {closeBlockedText(closeBlockers(p))}
              </span>
            ) : (
              <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setClosing(true)}>Zamknij sprawę</Button>
            )}
          </>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => m.reopen.mutate({ id: p.id })}>Otwórz ponownie</Button>
        )}
        {/* Usunięcie całej sprawy — tylko koordynator przedsięwzięcia / dyrektor (can_delete). */}
        {p.can_delete && (
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600 ml-auto"
            loading={m.remove.isPending}
            title="Usuń całą sprawę wraz z pozycjami (koordynator / dyrektor)"
            onClick={() => {
              if (!window.confirm(
                `Usunąć sprawę „${p.type_name}” wraz z jej pozycjami (${p.items.length})? Operacja jest nieodwracalna.`,
              )) return;
              setDeleteErr(null);
              m.remove.mutate(p.id, { onError: (e) => setDeleteErr(apiErr(e)) });
            }}
          >
            Usuń sprawę
          </Button>
        )}
      </div>
      {deleteErr && (
        <p className="mt-1.5 text-[11px] text-red-600 dark:text-red-400" role="alert">{deleteErr}</p>
      )}
      {closing && !isClosed && (
        <CloseProceedingModal proceeding={p} connectionId={connectionId} onClose={() => setClosing(false)} />
      )}
      {composing && !isClosed && (
        <ItemComposer
          proceeding={p}
          connectionId={connectionId}
          scopeLabel={scopeLabel}
          onClose={() => setComposing(false)}
        />
      )}
      <Modal
        open={addingScope && !isClosed}
        onClose={closeAddScope}
        title={ownerScope ? "Dodaj właścicieli (działki) do sprawy" : "Dodaj działki do sprawy"}
        size="lg"
      >
        <ProceedingTargetBanner proceeding={p} scopeLabel={scopeLabel} />
        <div className="space-y-2">
          <ScopePicker
            items={addableScopeItems}
            selected={selScope}
            onToggle={toggleScope}
            onToggleMany={toggleManyScope}
            placeholder={ownerScope ? "Filtruj właścicieli…" : "Filtruj działki…"}
          />
          <div className="flex justify-end gap-2 pt-3 border-t border-[var(--color-border-muted)]">
            <Button size="sm" variant="ghost" onClick={closeAddScope}>Anuluj</Button>
            <Button size="sm" disabled={selScope.size === 0 || m.update.isPending} loading={m.update.isPending} onClick={addScope}>
              Dodaj do sprawy{selScope.size > 0 ? ` (${selScope.size})` : ""}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function CorrespondenceTab({ connection }: { connection: PowerProject }) {
  const user = useAuthStore((s) => s.user);
  // Deep link ze „!" przy statusie działki (Działki / szuflada działki): ?proceeding=<id>.
  const [searchParams] = useSearchParams();
  const focusedProceedingId = searchParams.get("proceeding");
  const [scopeFilter, setScopeFilter] = useState<"" | ProceedingScope>("");
  const [statusFilter, setStatusFilter] = useState<"" | "open" | "closed">("");

  const listFilters = useMemo(() => {
    const f: Record<string, string> = { connection: connection.id };
    if (scopeFilter) f.scope = scopeFilter;
    if (statusFilter) f.status = statusFilter;
    return f;
  }, [connection.id, scopeFilter, statusFilter]);

  const { data: cases, isLoading } = useProceedings(listFilters);
  const { data: types } = useProceedingTypes(true);
  const { data: offices } = useSelectableOffices();
  const { data: parcels } = useParcels(connection.id);
  const { data: variants } = useVariants(connection.id);
  const m = useProceedingMutations(connection.id);

  // Per-project approve/submit gate — mirrors backend `_can`, so przedsięwzięcie
  // coordinators are covered without an explicit grant from panel "Metadane".
  const { canSubmit, canApprove } = useMyProjectGate(connection.id);
  const isDirector = user?.role === "director" || !!user?.is_ceo;

  const [creating, setCreating] = useState(false);
  const [scope, setScope] = useState<ProceedingScope>("office");
  const [typeId, setTypeId] = useState("");
  const [typeNote, setTypeNote] = useState(""); // "Inny" description
  const [officeId, setOfficeId] = useState("");
  const [addingOffice, setAddingOffice] = useState(false);
  const [routeVersionId, setRouteVersionId] = useState("");
  const [kpaDays, setKpaDays] = useState("45");
  // „Dodaj do ponagleń": null = domyślnie wg typu (zaznaczone, chyba że typ zakłada system).
  const [controlCalls, setControlCalls] = useState<boolean | null>(null);
  const [wholeProject, setWholeProject] = useState(false); // default: a specific scope
  const [selOwners, setSelOwners] = useState<Set<string>>(new Set());
  const [selParcels, setSelParcels] = useState<Set<string>>(new Set());

  const routeVersions: RouteVersionOpt[] = useMemo(
    () => (variants ?? []).flatMap((v) => v.versions.map((ver) => ({ id: ver.id, label: ver.label }))),
    [variants],
  );
  const ownerItems = useMemo<OwnerItem[]>(
    () => (parcels ?? []).flatMap((p) => p.owners.map((o) => ({
      id: o.id, office: o.office != null, label: `${o.full_name}${o.office ? " (urząd)" : ""} · ${p.teryt}`,
    }))),
    [parcels],
  );
  // owner id → its parcel, so a thread's owners collapse to "N działek" + a list of which.
  const ownerParcelInfo = useMemo(() => {
    const map = new Map<string, OwnerParcelInfo>();
    for (const p of parcels ?? []) {
      for (const o of p.owners) {
        map.set(o.id, { parcelId: p.id, teryt: p.teryt, street: p.street_and_number, fullName: o.full_name });
      }
    }
    return map;
  }, [parcels]);
  const parcelItems = useMemo(
    () => (parcels ?? []).filter((p) => p.in_current_route || p.is_manual).map((p) => ({ id: p.id, label: p.teryt })),
    [parcels],
  );
  const parcelTeryt = useMemo(() => new Map((parcels ?? []).map((p) => [p.id, p.teryt])), [parcels]);
  const officeOptions = useMemo(
    () => (offices ?? []).map((o) => ({ value: o.id, label: `${o.name} (${o.kind_display})` })),
    [offices],
  );

  const isOther = typeId === "__other__";
  // Typy zakładane automatycznie (wniosek, WPW, zawiadomienie/list) mają ponaglenie
  // w rejestrze źródła — dla nich checkbox startuje odznaczony, dla reszty zaznaczony.
  const selectedType = (types ?? []).find((t) => t.id === typeId);
  const controlCallsOn = controlCalls ?? !(selectedType?.automatic ?? false);
  // TECHNICAL → parcels scope; OWNER/OFFICE → owners scope (cross-parcel for OFFICE).
  const ownerScope = scope === "owner" || scope === "office";
  // Narrow the owners to the ownership type this scope targets: OFFICE → authority
  // owners (urząd), OWNER (WPW) → private owners (prywatna).
  const scopedOwnerItems = useMemo(() => ownersForScope(ownerItems, scope), [ownerItems, scope]);

  const resetForm = () => {
    setCreating(false); setScope("office"); setTypeId(""); setTypeNote("");
    setOfficeId(""); setRouteVersionId(""); setKpaDays("45"); setControlCalls(null);
    setWholeProject(false); setSelOwners(new Set()); setSelParcels(new Set());
  };

  const buildPayload = (typeIdToUse: string) => {
    const payload: Parameters<typeof m.create.mutate>[0] = {
      connection: connection.id,
      scope,
      type: typeIdToUse,
      type_note: isOther ? typeNote.trim() : "",
      recipient_office: officeId || null,
      route_version: routeVersionId || null,
      kpa_days: Number(kpaDays) || 45,
      control_calls: controlCallsOn,
    };
    if (!wholeProject) {
      if (ownerScope) payload.owners = [...selOwners];
      else payload.parcels = [...selParcels];
    }
    return payload;
  };

  const create = () => {
    if (isOther) {
      if (!typeNote.trim()) return;
      // "Inny (wpisz opis)": idempotent get-or-create a ProceedingType from the description.
      m.createType.mutate(typeNote.trim(), {
        onSuccess: (t) => m.create.mutate(buildPayload(t.id), { onSuccess: resetForm }),
      });
    } else if (typeId) {
      m.create.mutate(buildPayload(typeId), { onSuccess: resetForm });
    }
  };

  const toggle = (set: React.Dispatch<React.SetStateAction<Set<string>>>) => (id: string) =>
    set((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const toggleMany = (set: React.Dispatch<React.SetStateAction<Set<string>>>) => (ids: string[], value: boolean) =>
    set((prev) => {
      const next = new Set(prev);
      if (value) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });

  return (
    <div className="space-y-4">
      <SectionCard
        title="Sprawy / Korespondencja (postępowania: właściciel / urząd / techniczne)"
        action={
          <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
            + Nowe postępowanie
          </Button>
        }
      >
        {addingOffice && (
          <OfficeFormModal
            office={null}
            onClose={() => setAddingOffice(false)}
            onCreated={(o) => { setOfficeId(o.id); setAddingOffice(false); }}
          />
        )}

        <Modal
          open={creating}
          // Escape also reaches this modal while the nested "dodaj urząd" one is open —
          // closing it must not throw away the half-filled proceeding form.
          onClose={() => { if (!addingOffice) resetForm(); }}
          title="Nowe postępowanie"
          size="lg"
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="section-label">Zakres postępowania</span>
              <select className="input-base mt-1" value={scope} onChange={(e) => { setScope(e.target.value as ProceedingScope); setWholeProject(false); setSelOwners(new Set()); setSelParcels(new Set()); }}>
                {PROCEEDING_SCOPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="section-label">Typ sprawy</span>
              <ProceedingTypeSelect
                types={types ?? []}
                value={typeId}
                // Nowy typ = nowa wartość domyślna „Dodaj do ponagleń".
                onChange={(v) => { setTypeId(v); setControlCalls(null); }}
                canEdit={isDirector}
                onRename={(id, name) =>
                  new Promise<void>((resolve, reject) =>
                    m.updateType.mutate({ id, name }, { onSuccess: () => resolve(), onError: reject }),
                  )
                }
              />
            </label>

            {isOther && (
              <label className="block sm:col-span-2">
                <span className="section-label">Opis typu „Inny"</span>
                <input className="input-base mt-1" placeholder="np. Wniosek o uzgodnienie…" value={typeNote} onChange={(e) => setTypeNote(e.target.value)} />
              </label>
            )}

            <div className="block">
              <div className="flex items-center justify-between gap-2">
                <span className="section-label">Urząd / instytucja</span>
                <button
                  type="button"
                  className="section-label normal-case tracking-normal text-[var(--color-primary)] hover:underline"
                  onClick={() => setAddingOffice(true)}
                >
                  + dodaj urząd
                </button>
              </div>
              <div className="mt-1">
                <SearchableSelect options={officeOptions} value={officeId} placeholder="— wybierz —" onChange={(e) => setOfficeId(e.target.value)} />
              </div>
            </div>

            <label className="block">
              <span className="section-label">Dotyczy wersji trasy</span>
              <select className="input-base mt-1" value={routeVersionId} onChange={(e) => setRouteVersionId(e.target.value)}>
                <option value="">— brak —</option>
                {routeVersions.map((rv) => <option key={rv.id} value={rv.id}>Wersja {rv.label}</option>)}
              </select>
            </label>

            <label className="block">
              <span className="section-label">Termin KPA (dni)</span>
              <input type="number" min={1} className="input-base mt-1" value={kpaDays} onChange={(e) => setKpaDays(e.target.value)} />
            </label>

            <div className="block">
              <span className="section-label">Ponaglenia</span>
              <label className="mt-2 flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="checkbox" checked={controlCallsOn} onChange={(e) => setControlCalls(e.target.checked)} />
                Dodaj do ponagleń
              </label>
              <p className="mt-1 text-[10px] font-mono text-[var(--color-text-muted)]">
                {selectedType?.automatic
                  ? "Ten typ system zakłada sam (np. przy wysyłce wniosku) — ponaglenie ma zwykle w rejestrze źródła."
                  : `Ponaglenie ${Number(kpaDays) || 45} dni po wysłaniu pisma (bez pisma: od założenia sprawy) — dzwonisz Ty.`}
              </p>
            </div>

            <div className="sm:col-span-2 space-y-2">
              <label className="flex items-center gap-1.5 font-mono text-xs">
                <input type="checkbox" checked={wholeProject} onChange={(e) => setWholeProject(e.target.checked)} />
                Całe przedsięwzięcie (bez konkretnego zakresu)
              </label>
              {!wholeProject && (
                ownerScope ? (
                  <ScopePicker items={scopedOwnerItems} selected={selOwners} onToggle={toggle(setSelOwners)} onToggleMany={toggleMany(setSelOwners)} placeholder="Filtruj właścicieli (z wielu działek)…" />
                ) : (
                  <ScopePicker items={parcelItems} selected={selParcels} onToggle={toggle(setSelParcels)} onToggleMany={toggleMany(setSelParcels)} placeholder="Filtruj działki…" />
                )
              )}
              {ownerScope && (
                <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
                  {scope === "office"
                    ? "Postępowanie urzędowe może spinać właścicieli z wielu działek (wniosek zbiorczy); decyzja rozstrzyga wybranych."
                    : "Postępowanie z właścicielem (prywatne)."}
                </p>
              )}
              {scope === "technical" && (
                <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
                  Uzgodnienie techniczne (konserwator / gestor / ZUD) — nie wpływa na status działek.
                </p>
              )}
            </div>

            <div className="sm:col-span-2 flex justify-end gap-2 pt-3 border-t border-[var(--color-border-muted)]">
              <Button size="sm" variant="ghost" onClick={resetForm}>
                Anuluj
              </Button>
              <Button
                size="sm"
                disabled={(!typeId && !isOther) || (isOther && !typeNote.trim()) || m.create.isPending || m.createType.isPending}
                loading={m.create.isPending || m.createType.isPending}
                onClick={create}
              >
                Utwórz postępowanie
              </Button>
            </div>
          </div>
        </Modal>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <select className="input-base text-xs py-1 w-auto" value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value as "" | ProceedingScope)}>
            <option value="">Wszystkie zakresy</option>
            {PROCEEDING_SCOPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{PROCEEDING_SCOPE_LABEL[o.value]}</option>)}
          </select>
          <select className="input-base text-xs py-1 w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as "" | "open" | "closed")}>
            <option value="">Otwarte i zamknięte</option>
            <option value="open">Tylko otwarte</option>
            <option value="closed">Tylko zamknięte</option>
          </select>
        </div>

        {isLoading ? (
          <div className="py-6 flex justify-center"><Spinner /></div>
        ) : (cases ?? []).length === 0 ? (
          <EmptyState>Brak postępowań. Załóż nowe powyżej.</EmptyState>
        ) : (
          <div className="space-y-3">
            {(cases ?? []).map((p) => (
              <ProceedingCard
                key={p.id}
                p={p}
                connectionId={connection.id}
                ownerOptions={ownerItems}
                parcelOptions={parcelItems}
                ownerParcelInfo={ownerParcelInfo}
                parcelTeryt={parcelTeryt}
                projectId={connection.project}
                canApprove={canApprove}
                canSubmit={canSubmit}
                focused={p.id === focusedProceedingId}
              />
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
